/**
 * Client-side AI Note Generation Service
 * 
 * Calls Firebase Cloud Functions which proxy to Gemini API.
 * API key is stored server-side in Firebase Secrets — never exposed to client.
 * Every request is authenticated with Firebase Auth ID token.
 */

import { getServiceTemplate, getBillingCode } from "./Servicetemplates";
import { auth, db } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";
import {
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

// ═══════════════════════════════════════════════════════════════════
// CLOUD FUNCTION BASE URL
// After deploying, this auto-detects from your Firebase project ID.
// Or set REACT_APP_FUNCTIONS_URL in .env to override.
// ═══════════════════════════════════════════════════════════════════
const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_URL || "";

function getFunctionUrl(name) {
  if (FUNCTIONS_BASE) return `${FUNCTIONS_BASE}/${name}`;
  const projectId = auth.app?.options?.projectId;
  if (projectId) return `https://us-central1-${projectId}.cloudfunctions.net/${name}`;
  return `/${name}`;
}

async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}

async function callFunction(functionName, body) {
  const token = await getAuthToken();
  const url = getFunctionUrl(functionName);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Function error ${response.status}: ${errorData.error || "Unknown"}`);
  }

  return response.json();
}


// ═══════════════════════════════════════════════════════════════════
// BILLING CONFIG
// ═══════════════════════════════════════════════════════════════════

function getCachedBillingConfig() {
  try {
    const cached = localStorage.getItem("fieldnotes_billing_config");
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
}

function formatBillingCodeFromConfig(serviceType) {
  const config = getCachedBillingConfig();
  if (!config || !config.services?.[serviceType]) {
    return getBillingCode(serviceType);
  }
  const svc = config.services[serviceType];
  const format = config.codeFormat || "hcpcs";
  if (format === "hcpcs") return `${svc.hcpcsCode} — ${svc.label}`;
  if (format === "dbhdd") return `${svc.dbhddCode} — ${svc.label}`;
  return `${svc.hcpcsCode} (${svc.dbhddCode}) — ${svc.label}`;
}


// ═══════════════════════════════════════════════════════════════════
// SYSTEM PROMPT BUILDER
// ═══════════════════════════════════════════════════════════════════

function buildSystemPrompt(serviceType) {
  const template = getServiceTemplate(serviceType);
  const staffTitle = template.staffTitle;

  return `You are a clinical documentation specialist for behavioral health services in Georgia, operating under DBHDD (Department of Behavioral Health and Developmental Disabilities) documentation standards.

Your task is to generate a complete General Progress Note - Clinical from a session transcript.
This is a ${serviceType} session, billed under ${template.billingCode} (${template.billingLabel}).

CRITICAL — SPEAKER IDENTIFICATION:
The transcript is a raw stream from a single microphone capturing BOTH the ${staffTitle} and the Individual (patient/client). You MUST infer who is speaking based on context clues:

${staffTitle.toUpperCase()} typically:
- Asks questions ("How have you been?", "Have you been taking your medications?", "When is your next appointment?")
- Gives instructions or education ("It's important to take your medication daily", "Let's review your safety plan")
- Makes observations ("I notice you seem...", "Your apartment looks...")
- Discusses logistics ("I'll schedule your next appointment", "Let me help you with that")
- Uses clinical/professional language
- Redirects conversation to treatment topics
- Offers resources and referrals

THE INDIVIDUAL typically:
- Answers questions about their life, feelings, experiences
- Shares personal stories, complaints, concerns
- Talks about family, housing, legal issues, substance use history
- Uses casual/personal language
- Expresses emotions, frustrations, hopes
- Reports on their own behavior ("I've been taking my meds", "I went to the meeting")
- Makes direct personal statements ("I feel good", "I'm worried about...")

DOCUMENTATION RULES:
- When the INDIVIDUAL speaks, document as: "Ind stated...", "Ind reported...", "Ind verbalized...", "Ind disclosed...", "Ind denied..."
- When ${staffTitle.toUpperCase()} speaks/acts, document as: "${staffTitle} asked...", "${staffTitle} educated...", "${staffTitle} reviewed...", "${staffTitle} assisted...", "${staffTitle} transported..."
- When describing observations STAFF made, document as: "Ind appeared...", "Ind presented with...", "Ind was observed to...", "Ind demonstrated...", "Ind maintained..."
- ALWAYS distinguish between what Ind SELF-REPORTED vs what Staff OBSERVED
- Use "Ind" to refer to the individual throughout, never their name in the narrative sections
- If the individual makes a particularly significant statement, you may include a brief direct quote

CRITICAL — ANTI-HALLUCINATION RULES:
You MUST follow these rules to prevent dangerous clinical misinformation:

1. MEDICATIONS: ONLY include specific medication names if they were EXPLICITLY SPOKEN in the transcript. If the transcript says "I've been taking my meds" without naming specific medications, write "Ind reported taking medications as prescribed" — do NOT insert specific drug names.

2. DIAGNOSES: NEVER invent or assume diagnoses. Only reference specific diagnoses if the transcript explicitly mentions them.

3. DOSAGES: NEVER fabricate dosage amounts. If a medication dose was not stated in the transcript, do not include one.

4. QUOTES: Only use direct quotes if the words were clearly present in the transcript.

5. EVENTS AND DISCLOSURES: Only document events that were actually discussed. Do not fill in plausible details.

6. CLINICAL OBSERVATIONS: Only document observable behaviors if the transcript provides enough context to reasonably infer them.

7. MEDICATION ADHERENCE: If medications were not discussed at all, set medicationAdherence to "N/A" — do NOT default to "Yes."

8. MEDICAL CONDITIONS: If medical conditions were not discussed, set medicalConditions to "N/A."

9. WHEN IN DOUBT: Use "Ind did not discuss [topic]" or "Not addressed in this session" rather than generating plausible-sounding clinical content.

10. CONFIDENCE MARKERS: If you are uncertain about any detail, prefix it with "[VERIFY]".

CLINICAL LANGUAGE STANDARDS:
- Affect: flat, blunted, constricted, appropriate, labile, euphoric, dysphoric
- Speech: normal rate/rhythm/volume, pressured, soft, loud, tangential, circumstantial, organized
- Behavior: cooperative, guarded, agitated, calm, restless, psychomotor retardation
- Thought process: logical, goal-directed, disorganized, tangential, circumstantial
- Grooming/hygiene: appropriate, adequate, disheveled, malodorous, well-groomed
- Psychosis: responding to internal stimuli, paranoid ideation, grandiosity, looseness of associations

${template.promptAdditions}

REQUIRED DOCUMENTATION ELEMENTS FOR THIS SERVICE TYPE (${template.billingCode}):
${template.requiredElements.map(e => `- ${e}`).join("\n")}

TYPICAL INTERVENTIONS FOR ${serviceType.toUpperCase()}:
${template.typicalInterventions.map(i => `- ${i}`).join("\n")}

GEORGIA DBHDD SPECIFIC:
- Include all required fields for Medicaid audit compliance
- Use standard clinical terminology consistent with DSM-5
- Document any safety concerns per state reporting requirements
- Ensure documentation supports time billed (15-min unit increments)
- Every intervention should connect to a treatment plan goal

Return ONLY valid JSON with this exact structure, no markdown, no backticks, no explanation:
{
  "presenting": "Today's presenting issues paragraph. Start with how/where Ind was met by ${staffTitle} and initial presentation.",
  "livingStatus": "One of: Independent Living - With family | Independent Living - Individual owns or leases | Homeless | Residential facility | Supported Housing",
  "suicideRisk": "No",
  "suicideRiskSteps": {
    "step1": "Mood check, SI/HI assessment.",
    "step2": "Safety plan review.",
    "step3": "Treatment engagement status."
  },
  "medicationAdherence": "Yes or No or N/A",
  "medicalConditions": "Yes or No or N/A",
  "interventions": ["array of service-specific interventions"],
  "response": "Detailed chronological narrative (minimum 150 words).",
  "progress": "Progress toward treatment plan goals.",
  "selfRating": "1-6 scale",
  "proudOf": "What Ind expressed pride about.",
  "newProblems": "Yes or No",
  "servicePlan": "${template.goalLanguage}",
  "additionalComments": ""
}`;
}


// ═══════════════════════════════════════════════════════════════════
// MAIN NOTE GENERATION
// ═══════════════════════════════════════════════════════════════════

export async function generateNoteFromTranscript(transcript, sessionInfo = {}) {
  const durationMinutes = sessionInfo.duration ? Math.round(sessionInfo.duration / 60) : 30;
  const billingUnits = Math.ceil(durationMinutes / 15);
  const serviceType = sessionInfo.serviceType || "ICM Weekly Visit";
  const template = getServiceTemplate(serviceType);
  const staffTitle = template.staffTitle;

  const systemPrompt = buildSystemPrompt(serviceType);

  const userPrompt = `Generate a clinical progress note from this ${serviceType} session transcript.

SESSION INFO:
- Individual: ${sessionInfo.individualName || "Individual"}
- Date: ${sessionInfo.date || new Date().toLocaleDateString()}
- Location: ${sessionInfo.locationType || "Out-of-Clinic"} — ${sessionInfo.location || "Community"}
- Clinician: ${sessionInfo.clinician || staffTitle}
- Duration: ${durationMinutes} minutes (${billingUnits} billing units)
- Service Type: ${serviceType}
- Billing Code: ${template.billingCode} (${template.billingLabel})

${sessionInfo.knownMedications?.length > 0 ? `KNOWN MEDICATIONS ON FILE (REFERENCE ONLY — do NOT include unless EXPLICITLY discussed):
${sessionInfo.knownMedications.join(", ")}
` : ""}
${sessionInfo.knownDiagnoses?.length > 0 ? `KNOWN DIAGNOSES ON FILE (REFERENCE ONLY — do NOT include unless EXPLICITLY discussed):
${sessionInfo.knownDiagnoses.join(", ")}
` : ""}

SESSION TRANSCRIPT:
${transcript}

${sessionInfo.flaggedNotes?.length > 0 ? `
FLAGGED NOTES:
${sessionInfo.flaggedNotes.map(n => `[${n.time || ""}]: ${n.text || n.transcript || ""}`).join("\n")}
` : ""}

Generate the complete clinical note as JSON only.`;

  // Helper: parse Gemini output + apply safety override + build the note.
  // This runs regardless of how the raw text was obtained (Firestore-triggered
  // or, in the future, any other path).
  function buildNoteFromGeneratedText(generatedText) {
    const cleaned = generatedText.replace(/```json|```/g, "").trim();
    const noteContent = JSON.parse(cleaned);

    // SAFETY OVERRIDE
    const lowerTranscript = transcript.toLowerCase();
    const siPhrases = [
      "kill myself", "wanna die", "want to die", "wish i was dead",
      "better off dead", "don't want to live", "end it all", "about to kill myself",
      "going to kill myself", "gonna kill myself", "suicidal", "suicide",
      "hurt myself", "harm myself", "cutting myself", "overdose",
      "might kill myself", "ready to die",
    ];
    const hiPhrases = [
      "kill you", "kill him", "kill her", "kill them", "kill somebody",
      "kill someone", "hurt someone", "hurt somebody", "gonna kill",
      "going to kill", "about to kill", "want to kill", "homicidal",
    ];
    const hasSI = siPhrases.some(p => lowerTranscript.includes(p));
    const hasHI = hiPhrases.some(p => lowerTranscript.includes(p));

    if (hasSI || hasHI) {
      noteContent.suicideRisk = "Yes";
      if (!noteContent.additionalComments) noteContent.additionalComments = "";
      const flags = [];
      if (hasSI) flags.push("suicidal ideation");
      if (hasHI) flags.push("homicidal ideation");
      noteContent.additionalComments += `\n⚠️ SAFETY OVERRIDE: ${flags.join(" and ")} language detected in transcript. Suicide risk automatically set to "Yes" regardless of context or retraction. Clinical review required.`;
      noteContent.additionalComments = noteContent.additionalComments.trim();
    }

    return {
      header: {
        individual: sessionInfo.individualName || "Individual",
        dob: sessionInfo.dob || "",
        date: sessionInfo.date || new Date().toLocaleDateString(),
        clinician: sessionInfo.clinician || staffTitle,
        location: sessionInfo.locationType || "Out-of-Clinic",
        site: sessionInfo.location || "Community",
        serviceType: serviceType,
        duration: `${durationMinutes} minutes`,
      },
      ...noteContent,
      billingCode: formatBillingCodeFromConfig(serviceType),
      billingUnits: `${billingUnits} units (${durationMinutes} min)`,
      confidence: calculateConfidence(transcript, noteContent),
    };
  }

  // Generate via Firestore-triggered Cloud Function.
  //   1. Client writes pendingNotes/{noteId} with status: "queued".
  //   2. processPendingNote Firestore trigger fires, runs Gemini, writes
  //      back status: "complete" + generatedText (or status: "failed").
  //   3. Client subscribes via onSnapshot. Firestore's offline persistence
  //      delivers the update whenever the client is connected — survives
  //      tab backgrounding and full reloads.
  const userId = auth.currentUser?.uid;
  if (!userId) {
    return { success: false, error: "Not authenticated" };
  }

  // Unique doc per call so retries don't collide. sessionId can be missing
  // (recovery-page-triggered generation) — fall back to a timestamp.
  const sessionTag = sessionInfo.sessionId || `session-${Date.now()}`;
  const noteDocId = `${userId}_${sessionTag}_${Date.now()}`;
  const docRef = orgDoc("pendingNotes", noteDocId);

  try {
    await setDoc(docRef, {
      agentId: userId,
      sessionId: sessionInfo.sessionId || null,
      systemPrompt,
      userPrompt,
      status: "queued",
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[Generation] Failed to write pendingNote doc:", e);
    return { success: false, error: "Failed to queue note: " + e.message };
  }

  return new Promise((resolve) => {
    let unsubscribe = null;
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      if (unsubscribe) {
        try { unsubscribe(); } catch (e) {}
        unsubscribe = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      // Best-effort cleanup. Don't block resolution on this.
      if (result.success) {
        deleteDoc(docRef).catch(() => {});
      }
      resolve(result);
    };

    unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (!data) return;

        if (data.status === "complete" && data.generatedText) {
          try {
            const note = buildNoteFromGeneratedText(data.generatedText);
            settle({ success: true, note });
          } catch (parseErr) {
            settle({
              success: false,
              error: "Failed to parse generated note: " + parseErr.message,
            });
          }
        } else if (data.status === "failed") {
          settle({
            success: false,
            error: data.error || "Generation failed on server",
          });
        }
        // status === "queued" or "processing" — keep waiting
      },
      (err) => {
        console.error("[Generation] onSnapshot error:", err);
        settle({
          success: false,
          error: "Subscription error: " + (err.message || "unknown"),
        });
      }
    );

    // Hard timeout: if the function takes longer than 5 minutes,
    // resolve with an error and let the user retry.
    timeoutId = setTimeout(() => {
      settle({
        success: false,
        error: "Generation timed out after 5 minutes. The note may still complete in the background — check Notes shortly.",
      });
    }, 5 * 60 * 1000);
  });
}

function calculateConfidence(transcript, noteContent) {
  let score = 70;
  const wordCount = transcript.split(" ").length;
  if (wordCount > 100) score += 10;
  if (wordCount > 300) score += 5;
  if (noteContent.presenting) score += 2;
  if (noteContent.interventions?.length > 3) score += 3;
  if (noteContent.response?.length > 100) score += 3;
  if (noteContent.progress) score += 2;
  return Math.min(score, 98);
}


// ═══════════════════════════════════════════════════════════════════
// REAL-TIME FACT EXTRACTION
// ═══════════════════════════════════════════════════════════════════

export async function extractKeyFacts(transcriptChunk, previousFacts = []) {
  if (!transcriptChunk || transcriptChunk.trim().length < 20) {
    return { success: true, facts: previousFacts };
  }

  const prompt = `You are extracting key clinical facts from an ICM session transcript chunk.

PREVIOUSLY EXTRACTED FACTS:
${previousFacts.length > 0 ? previousFacts.map((f, i) => `${i + 1}. [${f.category}] ${f.text}`).join("\n") : "None yet."}

NEW TRANSCRIPT CHUNK:
${transcriptChunk}

Extract any NEW key clinical facts. Categories: MEDICATION, SAFETY, HOUSING, LEGAL, SUBSTANCE, SYMPTOM, APPOINTMENT, QUOTE, OBSERVATION, OTHER

Respond ONLY with a JSON array: [{"category": "...", "text": "..."}]
If no new facts, respond with []`;

  try {
    const result = await callFunction("extractFacts", { prompt });
    if (!result.success) throw new Error(result.error);

    const cleaned = result.text.replace(/```json|```/g, "").trim();
    const newFacts = JSON.parse(cleaned);
    return { success: true, facts: [...previousFacts, ...newFacts] };
  } catch (e) {
    console.warn("Fact extraction failed (non-critical):", e);
    return { success: false, facts: previousFacts };
  }
}


// ═══════════════════════════════════════════════════════════════════
// CONVERSATIONAL ONBOARDING
// ═══════════════════════════════════════════════════════════════════

export async function extractIndividualFromConversation(conversationText, existingData = {}) {
  const prompt = `You are an intake coordinator AI for an ICM behavioral health program in Georgia.

CONVERSATION SO FAR:
${conversationText}

${Object.keys(existingData).length > 0 ? `ALREADY CAPTURED:\n${JSON.stringify(existingData, null, 2)}` : ""}

Extract and return a JSON object with fields: firstName, lastName, dob, gender, phone, email, address, county, emergencyContact, emergencyPhone, emergencyRelation, diagnoses[], medications[], riskLevel, allergies[], medicaidId, insuranceProvider, program, contactFrequency, assignToAgent, onboardingNotes, specialConsiderations, missingRequired[], nextQuestion.

Use null for anything not mentioned. REQUIRED: firstName, lastName, dob, address, program, diagnoses.
Respond ONLY with valid JSON.`;

  try {
    const result = await callFunction("aiCall", { prompt, maxTokens: 2000 });
    if (!result.success) throw new Error(result.error);

    const cleaned = result.text.replace(/```json|```/g, "").trim();
    const extracted = JSON.parse(cleaned);

    const merged = { ...existingData };
    for (const [key, value] of Object.entries(extracted)) {
      if (value !== null && value !== undefined && value !== "" && 
          !(Array.isArray(value) && value.length === 0)) {
        merged[key] = value;
      }
    }

    return {
      success: true,
      data: merged,
      missingRequired: extracted.missingRequired || [],
      nextQuestion: extracted.nextQuestion || null,
    };
  } catch (e) {
    console.error("Individual extraction failed:", e);
    return { success: false, data: existingData, error: e.message };
  }
}


export async function extractUpdatesFromConversation(conversationText, existingRecord) {
  const prompt = `You are an AI assistant for an ICM behavioral health program.

CURRENT RECORD:
Name: ${existingRecord.firstName} ${existingRecord.lastName}
DOB: ${existingRecord.dob}
Address: ${existingRecord.address}
Phone: ${existingRecord.phone}
Diagnoses: ${(existingRecord.diagnoses || []).join(", ")}
Medications: ${(existingRecord.medications || []).join(", ")}
Risk Level: ${existingRecord.riskLevel}
Program: ${existingRecord.program}
Emergency Contact: ${existingRecord.emergencyContact} (${existingRecord.emergencyRelation}) ${existingRecord.emergencyPhone}

WHAT THE AGENT SAID:
${conversationText}

Extract ONLY changed fields. Return JSON: { "updates": {}, "summary": "", "confirmationMessage": "" }
Respond ONLY with valid JSON.`;

  try {
    const result = await callFunction("aiCall", { prompt, maxTokens: 1500 });
    if (!result.success) throw new Error(result.error);

    const cleaned = result.text.replace(/```json|```/g, "").trim();
    const parsed = JSON.parse(cleaned);

    return {
      success: true,
      updates: parsed.updates || {},
      summary: parsed.summary || "",
      confirmationMessage: parsed.confirmationMessage || "",
    };
  } catch (e) {
    console.error("Update extraction failed:", e);
    return { success: false, error: e.message };
  }
}


export async function getOnboardingChatResponse(messages, extractedData) {
  const missing = extractedData?.missingRequired || [];
  const hasData = extractedData?.data || {};
  
  const systemPrompt = `You are a friendly, efficient intake coordinator AI for an ICM behavioral health program. You're helping an agent add a new individual to their caseload through conversation.

Your personality: professional but warm, concise, never robotic. Ask one question at a time.

CAPTURED SO FAR:
${JSON.stringify(hasData, null, 2)}

STILL MISSING (required): ${missing.length > 0 ? missing.join(", ") : "All required fields captured!"}

RULES:
- If all required fields captured, offer to save
- Ask about most important missing field naturally
- Keep responses to 1-3 sentences
- If agent says "that's it" or "save it", confirm ready to save`;

  try {
    const result = await callFunction("onboardingChat", { messages, systemPrompt });
    if (!result.success) throw new Error(result.error);
    return { success: true, message: result.text };
  } catch (e) {
    console.error("Chat response failed:", e);
    return { success: false, message: "I'm having trouble connecting. Could you try again?" };
  }
}